/**
 * Messenger — transporte `postMessage` host ↔ iframe.
 *
 * Seguridad (§2 invariantes, AC6):
 *  - Todo mensaje saliente usa `targetOrigin` = origen permitido (no `'*'`).
 *  - El listener único filtra por `event.source === iframe.contentWindow` **y**
 *    `event.origin ∈ allowedOrigins`. Mensajes de origen no autorizado se
 *    ignoran en silencio (no log, no callback) para no exponer superficies.
 *  - Handshake: al recibir `finva:ready` se envía `finva:init` con el `InitPayload`.
 *
 * El messenger no conoce `SubmitData` ni callbacks de negocio: solo enruta
 * mensajes tipados. El consumidor (modo embedded/bricks) traduce eventos a
 * callbacks. Esto mantiene la superficie mínima y testeable.
 */
import type {
  HostToIframe,
  IframeToHost,
  InitPayload,
} from './protocol';

export type IframeEventType = IframeToHost['type'];
export type IframeHandler = (payload: unknown) => void;

export interface MessengerOptions {
  /** `<iframe>` sobre el que se comunica. */
  iframe: HTMLIFrameElement;
  /** Orígenes autorizados a enviar/recibir (allowlist). El principal es el del iframe. */
  allowedOrigins: string[];
  /** Payload inicial enviado en el handshake `finva:ready` → `finva:init`. */
  initPayload: InitPayload;
}

export class Messenger {
  private readonly iframe: HTMLIFrameElement;
  private readonly allowedOrigins: Set<string>;
  private initPayload: InitPayload;
  private readonly handlers = new Map<IframeEventType, Set<IframeHandler>>();
  private listener: ((event: MessageEvent) => void) | null = null;
  private readySent = false;

  constructor(opts: MessengerOptions) {
    this.iframe = opts.iframe;
    this.allowedOrigins = new Set(opts.allowedOrigins.map(normalizeOrigin));
    this.initPayload = opts.initPayload;
  }

  /** Conecta el listener global de `message`. Idempotente. */
  connect(): void {
    if (this.listener) return;
    const listener = (event: MessageEvent): void => this.onMessage(event);
    window.addEventListener('message', listener);
    this.listener = listener;
  }

  /** Suscribe un handler a un tipo de evento del iframe. Devuelve un desuscriptor. */
  on(type: IframeEventType, handler: IframeHandler): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    set.add(handler);
    return () => {
      set?.delete(handler);
      if (set && set.size === 0) this.handlers.delete(type);
    };
  }

  /** Envía un mensaje host → iframe con `targetOrigin` restringido. */
  send(message: HostToIframe): void {
    const win = this.iframe.contentWindow;
    if (!win) return; // iframe aún no cargado: el handshake reiniciará al recibir ready.
    const targetOrigin = this.primaryOrigin();
    win.postMessage(message, targetOrigin);
  }

  /** Envía `finva:init` (idempotente: solo la primera vez tras un `ready`). */
  sendInit(): void {
    this.send({ type: 'finva:init', payload: this.initPayload });
  }

  /**
   * Reinicia el handshake para la próxima recarga del iframe (p. ej. cambio de
   * `brand` en `controller.update`): vuelve a permitir el envío de `finva:init`
   * al recibir el próximo `finva:ready` y, si se pasa, actualiza el
   * `initPayload`. **No** desconecta el listener ni limpia handlers — las
   * suscripciones del mount (`finva:resize`/`finva:close`) sobreviven a la
   * recarga, que es justamente el motivo por el que existe este método en lugar
   * de destruir y recrear el messenger.
   */
  resetHandshake(initPayload?: InitPayload): void {
    if (initPayload) this.initPayload = initPayload;
    this.readySent = false;
  }

  /** Desconecta el listener y libera handlers. Safe llamar varias veces. */
  destroy(): void {
    if (this.listener) {
      window.removeEventListener('message', this.listener);
      this.listener = null;
    }
    this.handlers.clear();
    this.readySent = false;
  }

  // --- internals ---

  private primaryOrigin(): string {
    // El primer origen de la allowlist es el del iframe (se construye así en bricks).
    return this.allowedOrigins.values().next().value ?? '*';
  }

  private onMessage(event: MessageEvent): void {
    // Filtro de source: solo aceptamos mensajes provenientes de NUESTRO iframe.
    if (event.source !== this.iframe.contentWindow) return;
    // Filtro de origin (AC6): ignorar silenciosamente orígenes no autorizados.
    if (!this.allowedOrigins.has(normalizeOrigin(event.origin))) return;

    const data = event.data as { type?: string } | null;
    if (!data || typeof data.type !== 'string') return;

    // Handshake: finva:ready → finva:init (una sola vez por conexión).
    if (data.type === 'finva:ready') {
      if (!this.readySent) {
        this.readySent = true;
        this.sendInit();
      }
    }

    this.dispatch(data.type as IframeEventType, (data as { payload?: unknown }).payload);
  }

  private dispatch(type: IframeEventType, payload: unknown): void {
    const set = this.handlers.get(type);
    if (!set) return;
    for (const handler of set) {
      try {
        handler(payload);
      } catch {
        // Un handler roto no debe romper a los demás ni al transporte.
      }
    }
  }
}

/** Normaliza un origin a `https://host[:port]` sin trailing slash. */
function normalizeOrigin(origin: string): string {
  return (origin ?? '').trim().replace(/\/$/, '');
}